<template>
  <div class="relative flex flex-col">
    <UiDialogHeader>
      <UiDialogTitle>{{ tg("title") }}</UiDialogTitle>
      <UiDialogDescription class="text-sm text-black/50">{{ tsafe("description") }}</UiDialogDescription>
    </UiDialogHeader>

    <UiTransition mode="out-in">
      <UiSkeleton
        v-if="isLoadingKycAnswers"
        loading
        :classes="{ line: 'h-20', container: 'gap-4' }"
        :repeat="3"
      />

      <div class="flex flex-col gap-3" v-else>
        <div
          class="flex items-center gap-10 rounded-sm bg-neutral-100 p-6 md:flex-col md:gap-6 md:p-3"
          v-if="kycAnswers?.pensionDepositPercentage && kycAnswers?.pensionDepositPercentage < 7"
        >
          <div class="flex-1 text-sm">
            <h3 class="font-medium">
              {{ t("pensionDepositPercentage.title") }}
            </h3>
            <p class="whitespace-pre-line font-light">
              {{
                t("pensionDepositPercentage.description", {
                  percentage: toPercent(kycAnswers?.pensionDepositPercentage),
                })
              }}
            </p>
          </div>
          <UiSwitchTabs
            class="min-md:h-7 w-40 md:w-full"
            v-bind="registerField('updatePensionDepositPercentage')"
            :options="[
              { title: 'כן', value: true },
              { title: 'לא', value: false },
            ]"
          />
        </div>

        <div class="flex items-center gap-10 rounded-sm bg-neutral-100 p-6 md:flex-col md:gap-6 md:p-3">
          <div class="flex-1 text-sm">
            <h3 class="font-medium">
              {{ t("pensionProvisionAmount.title") }}
            </h3>
            <p class="flex-1 whitespace-pre-line text-sm font-light">
              {{
                t("pensionProvisionAmount.description", {
                  amount: toCash(kycAnswers?.pensionProvisionAmount, NIS),
                })
              }}
            </p>
          </div>
          <UiInput
            v-bind="registerField('pensionProvisionAmount')"
            :disabled="isPostAnswersMutationLoading"
            type="number"
            valueType="number"
            :min="0"
            :classes="{
              input: 'min-md:text-sm w-12 p-0 py-1 ps-2',
              root: 'min-md:h-7 w-40 bg-white md:h-9 md:w-full',
            }"
          >
            <template #append>
              <span class="min-md:text-sm select-none pe-2 ps-2 text-black/50">{{ NIS }}</span>
            </template>
          </UiInput>
        </div>

        <div class="flex items-center gap-10 rounded-sm bg-neutral-100 p-6 md:flex-col md:gap-6 md:p-3">
          <div class="flex-1 text-sm">
            <h3 class="font-medium">
              {{ t("educationReportedSalary.title") }}
            </h3>
            <p class="whitespace-pre-line font-light">
              {{
                t("educationReportedSalary.description", {
                  amount: toCash(kycAnswers?.educationReportedSalary, NIS),
                })
              }}
            </p>
          </div>
          <UiInput
            v-bind="registerField('educationReportedSalary')"
            :disabled="isPostAnswersMutationLoading"
            type="number"
            valueType="number"
            :min="0"
            :classes="{
              input: 'min-md:text-sm w-12 p-0 py-1 ps-2',
              root: 'min-md:h-7 w-40 bg-white md:h-9 md:w-full',
            }"
          >
            <template #append>
              <span class="min-md:text-sm select-none pe-2 ps-2 text-black/50">{{ NIS }}</span>
            </template>
          </UiInput>
        </div>
      </div>
    </UiTransition>

    <UiCheckbox
      v-model="disclaimerAccepted"
      :classes="{ root: 'mt-6', label: 'text-sm', icon: 'size-3', control: { root: 'size-4' } }"
      :label="t('disclaimer')"
    />

    <UiDialogFooter class="sticky bottom-0 justify-end bg-white py-4 pb-0 md:justify-center md:pb-4">
      <UiButton
        class="md:h-10 md:w-full"
        :loading="isPostAnswersMutationLoading"
        @click="handleDone"
        :disabled="!eagerValid || !disclaimerAccepted"
      >
        {{ isMobile ? $t("common.done") : $t("common.accept") }}
      </UiButton>
    </UiDialogFooter>
  </div>
</template>

<script setup lang="ts">
import { useMutation } from "@tanstack/vue-query"
import { boolean, nullish, number, object } from "valibot"

import { Kyc } from "@finq/kyc/composables/kyc-namespace"

const { t } = useI18n()
const { tg, tsafe } = useI18nUtils({ useScope: "local" })
const { isMobile } = useDisplay()
const props = defineProps<{ class?: ClassValue; onDone?: () => void }>()

const popup = usePopup()
const { t: $t } = useI18n({ useScope: "global" })

const disclaimerAccepted = ref(false)

const { data: kycAnswers, isLoading: isLoadingKycAnswers } = useFetchKycAnswersQuery<{
  pensionDepositPercentage: number
  educationReportedSalary: number
  pensionProvisionAmount: number
}>({
  questions: ["pensionDepositPercentage", "educationReportedSalary", "pensionProvisionAmount"],
})

const {
  registerField,
  state: formState,
  eagerValid,
  ...form
} = useForm(
  object({
    updatePensionDepositPercentage: nullish(boolean()),
    educationReportedSalary: number(),
    pensionProvisionAmount: number(),
  }),
  {
    defaultValues: computed(() => ({
      educationReportedSalary: kycAnswers?.value?.educationReportedSalary ?? 0,
      pensionProvisionAmount: kycAnswers?.value?.pensionProvisionAmount ?? 0,
      updatePensionDepositPercentage:
        kycAnswers.value?.pensionDepositPercentage && kycAnswers.value?.pensionDepositPercentage < 7
          ? true
          : null,
    })),
  }
)

const postAnswersMutation = useMutation({
  mutationKey: ["KycService.postAnswer"],
  mutationFn: KycService.sendAnswers,
})

const flowStageManager = usePensionFlowStageManager()
const { isStageLoading } = storeToRefs(flowStageManager)
const isPostAnswersMutationLoading = computed(
  () => postAnswersMutation.isPending.value || isStageLoading.value
)

async function handleDone() {
  if (!eagerValid.value) return

  try {
    await postAnswersMutation.mutateAsync({
      questionnaire: Kyc.QuestionnaireTypes.PENSIONS,
      chapter: "MISLAKA_DETAILS_VERIFICATION",
      page: "MISLAKA_DETAILS_VERIFICATION",
      phase: 2,
      isNext: true,
      answers: formState,
    })

    props.onDone?.()
  } catch (err) {
    popup.open({
      title: "שימו לב",
      description: "לא ניתן לשלוח את התשובות כעת, נסה שנית מאוחר יותר",
      errorObject: err,
    })
  }
}
</script>

<i18n lang="json">
{
  "he": {
    "title": {
      "male": "עוד רגע תוכל לצפות בתובנות שהכנו עבורך",
      "female": "עוד רגע תוכלי לצפות בתובנות שהכנו עבורך"
    },
    "description": "חשוב לנו רק לוודא איתך שהפרטים שקיבלנו מהמסלקה הפנסיונית אכן מדויקים. שלוש שאלות קצרות – ואנחנו שם!",
    "disclaimer": "ידוע לי כי התובנות אשר תוצגנה בפניי הן תוצר של ניתוח מערכת מבוססת בינה מלאכותית (AI) ואין לראות בהן ייעוץ או שיווק פנסיוני.",
    "pensionDepositPercentage": {
      "title": "תגמולי עובד",
      "description": "שיעור ההפקדה שלך לתגמולים הוא {percentage} מהשכר. אפשר להגדיל את ההפקדה ל־7% – מה שישפר את החיסכון הפנסיוני שלך. רוצה שנעדכן את שיעור ההפקדה ל־7%?"
    },
    "pensionProvisionAmount": {
      "title": "שכר לתנאים פנסיונים",
      "description": "השכר שלך לחישוב הפיצויים ותגמולים הוא {amount}. האם הסכום נכון? אם לא – אפשר לעדכן כאן:"
    },
    "educationReportedSalary": {
      "title": "שכר לקרן השתלמות",
      "description": "סכום ההפקדה שלך לקרן ההשתלמות מבוסס על {amount}. האם הסכום נכון? אם לא – אפשר לעדכן כאן:"
    }
  },
  "en": {}
}
</i18n>

<style lang="scss" scoped>
:deep(input[type="number"]) {
  text-align: right;
}
</style>
